import clsx from 'clsx';
import Link from 'next/link';
import { forwardRef } from 'react';
import { useScrollActive } from './hooks/useScrollActive';
import { useToggleLessMore } from './hooks/useToggleLessMore';
import { adaptProductInformation } from './SectionInformation.util';
import { ButtonLessMore } from './ui/ButtonLessMore';
import { CategoryItem } from './ui/CategoryItem';
import { FieldInformation } from './ui/FieldInformation';
import { GeneralInfomation } from './ui/GeneralInfomation';
import { Ingredient } from './ui/Ingredient';

interface Props {
  product: any;
}

const mappingTypeMedicineLabel: Record<string, string> = {
  "LALT0T": "Có", // Thuốc kê đơn
  "XT2U1Y": "Không" // Thuốc không kê đơn
}

export const SectionInformation = forwardRef<HTMLDivElement, Props>(({ product }, ref) => {
  const { onCategoryClick, activeId, setActiveId } = useScrollActive();

  const { categoryRef, contentRef, contentHeight, categoryHeight, shouldHaveExpand, toggle, setIsExpanded, isExpanded } = useToggleLessMore();

  if (!product) return null;

  const {
    contraindication,
    description,
    dosage,
    drugInteraction,
    drugOverdose,
    indication,
    storage,
    ingredients,
    categories,
    infoCategories,
    expiredDate,
    manufacturer,
    country,
    documents,
    isEmpty,
    registrationNumber,
    brand,
  } = adaptProductInformation(product);

  if (isEmpty) return null;

  const isMedicine = product?.sellerCategoryCode === "AQFV2U"// when sellerCategoryCode = "AQFV2U" this code is medicine type (Thuốc)

  return (
    <div ref={ref}>
      <section id="section-information" className="grid grid-cols-3 gap-5 p-5 rounded-[20px] bg-white mb-5">
        {!!infoCategories?.length && (
          <div className="col-span-1 relative">
            <ul ref={categoryRef} id="information-categories" className="flex flex-col gap-3 list-none pl-0 sticky top-[144px]">
              {infoCategories.map((item, index) => (
                <CategoryItem
                  key={index}
                  isActive={item.id === activeId}
                  label={item.label}
                  onClick={() => {
                    setIsExpanded(true);
                    setTimeout(() => {
                      onCategoryClick(item.id);
                    });
                  }}
                />
              ))}
            </ul>
          </div>
        )}
        <div className="col-span-2 relative">
          <div
            ref={contentRef}
            className={clsx('flex flex-col gap-2 overflow-hidden')}
            style={{
              height: isExpanded ? `auto` : `${categoryHeight}px`,
            }}
          >
            <GeneralInfomation id="description" value={description} title="Thông tin chung">
              <FieldInformation title="Hồ sơ sản phẩm" className="flex-col">
                {!!documents?.length && (
                  <div className="flex flex-col gap-1">
                    {documents.map((item, index) => (
                      <Link key={index} href={item.href}>
                        <a target="_blank" className="text-[var(--primary-color)]">
                          {item.label}
                        </a>
                      </Link>
                    ))}
                  </div>
                )}
              </FieldInformation>
              <FieldInformation title="Hạn sử dụng:">{!!expiredDate && <span>{expiredDate}</span>}</FieldInformation>
              <FieldInformation title="Danh mục:">
                {!!categories?.length &&
                  categories.map((item, index) => (
                    <Link key={index} href={item.href}>
                      <a target="_blank" className="text-[var(--primary-color)]">
                        {item.label}
                      </a>
                    </Link>
                  ))}
              </FieldInformation>
              <FieldInformation title="Nhà sản xuất:">
                {!!manufacturer && (
                  <Link href={manufacturer.href}>
                    <a target="_blank" className="text-[var(--primary-color)]">
                      {manufacturer.label}
                    </a>
                  </Link>
                )}
              </FieldInformation>
              <FieldInformation title="Nước sản xuất:">{!!country && <span>{country}</span>}</FieldInformation>
              <FieldInformation title="Thương hiệu:">
                {brand ? (
                  <Link href={brand?.href}>
                    <a target="_blank" className="text-[var(--primary-color)]">
                      {brand?.label}
                    </a>
                  </Link>
                ) : null}
              </FieldInformation>
              <FieldInformation title="Số đăng ký:">{!!registrationNumber && <span>{registrationNumber}</span>}</FieldInformation>
              {isMedicine && <FieldInformation title="Thuốc kê đơn:">{product.sellerSubCategoryCode && mappingTypeMedicineLabel[product.sellerSubCategoryCode as string]}</FieldInformation>}
            </GeneralInfomation>
            <Ingredient id="ingredients" title="Chi tiết thành phần" value={ingredients} />
            <GeneralInfomation id="indication" value={indication} title="Chỉ định" />
            <GeneralInfomation id="contraindication" value={contraindication} title="Chống chỉ định" />
            <GeneralInfomation id="dosage" value={dosage} title="Liều lượng - cách dùng" />
            <GeneralInfomation id="drugInteraction" value={drugInteraction} title="Tương tác thuốc" />
            <GeneralInfomation id="storage" value={storage} title="Bảo quản" />
            <GeneralInfomation id="drugOverdose" value={drugOverdose} title="Quá liều" />
          </div>

          {shouldHaveExpand && (
            <div
              className={clsx('flex justify-end items-end', [
                !isExpanded &&
                  'bg-[linear-gradient(180deg,_rgba(255,255,255,0)_0%,_rgba(255,255,255,0.3660057773109243)_24%,_rgba(255,255,255,1)_100%)]',
                !isExpanded && 'absolute bottom-0 left-0 right-0 h-[60px] cursor-pointer',
              ])}
              onClick={() => {
                toggle();
                if (isExpanded) {
                  setTimeout(() => {
                    setActiveId(infoCategories[0].id);
                  });
                }
              }}
            >
              <ButtonLessMore isExpanded={isExpanded} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
});

SectionInformation.displayName = 'SectionInformation';

